import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, X, Minus, ArrowRight, Mail, ShieldCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav, MarketingFooter, useSeoMeta, MAILTO } from "@/components/MarketingChrome";

const COLS = ["Storage targets", "Immutability model", "Built for 21 CFR Part 11 / GxP", "Zero-knowledge encryption"];

const ROWS = [
  {
    vendor: "Vaultvec", highlight: true,
    cells: [
      "Azure Blob / S3-compatible object storage",
      "Storage-enforced WORM + app-level lock",
      { icon: "yes", text: "Built-in — audit vault, GxP records, AI reports" },
      { icon: "yes", text: "Yes — client-side, keys never leave you" },
    ],
  },
  {
    vendor: "Veeam",
    cells: [
      "S3 / Azure Blob / hardened repo",
      "Storage-layer WORM (Object Lock / Blob WORM)",
      { icon: "no", text: "General-purpose (DIY compliance)" },
      { icon: "at-rest", text: "At-rest (storage-side)" },
    ],
  },
  {
    vendor: "Commvault",
    cells: [
      "S3 / Azure Blob",
      "Air-gap + WORM copies",
      { icon: "no", text: "General-purpose" },
      { icon: "at-rest", text: "At-rest (storage-side)" },
    ],
  },
  {
    vendor: "Rubrik",
    cells: [
      "Own appliance + cloud tier",
      "Immutable-by-architecture",
      { icon: "no", text: "General-purpose" },
      { icon: "at-rest", text: "At-rest (platform)" },
    ],
  },
  {
    vendor: "Cohesity",
    cells: [
      "Own platform + cloud tier",
      "WORM by architecture / snapshot lock",
      { icon: "no", text: "General-purpose" },
      { icon: "at-rest", text: "At-rest (platform)" },
    ],
  },
  {
    vendor: "Acronis",
    cells: [
      "Cloud / object storage",
      "Cloud storage immutability",
      { icon: "no", text: "General-purpose (+ endpoint security)" },
      { icon: "at-rest", text: "At-rest (storage-side)" },
    ],
  },
];

function Icon({ kind }) {
  if (kind === "yes") return <Check className="h-4 w-4 text-[hsl(var(--chart-2))] shrink-0" strokeWidth={2.5} />;
  if (kind === "no") return <X className="h-4 w-4 text-muted-foreground/40 shrink-0" strokeWidth={2} />;
  return <Minus className="h-4 w-4 text-[hsl(var(--chart-4))] shrink-0" strokeWidth={2} />;
}

function Cell({ value }) {
  if (typeof value === "string") return <span className="text-sm text-muted-foreground">{value}</span>;
  return <span className="flex items-start gap-2 text-sm text-muted-foreground"><Icon kind={value.icon} /> {value.text}</span>;
}

export default function VendorCompare() {
  useSeoMeta(
    "Vaultvec vs Veeam, Commvault, Rubrik, Cohesity & Acronis — Backup Comparison",
    "An honest comparison of Vaultvec against enterprise backup suites for pharma: storage targets, immutability/WORM, 21 CFR Part 11 compliance and zero-knowledge encryption."
  );
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />
      <MarketingNav />

      <section className="relative pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="ambient-spot top-0 right-[8%] w-[460px] h-[360px] opacity-50" />
        <div className="relative max-w-5xl mx-auto px-6">
          <span className="eyebrow-line font-mono text-xs tracking-[0.2em] uppercase text-primary">Comparison</span>
          <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.98]">How Vaultvec compares to enterprise backup</h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            An honest look at where Vaultvec fits against Veeam, Commvault, Rubrik, Cohesity and Acronis — and where it deliberately doesn't compete.
          </p>
        </div>
      </section>

      {/* Matrix */}
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="rounded-sm border border-white/10 overflow-x-auto">
          <table className="w-full min-w-[820px] text-left">
            <thead>
              <tr className="bg-[#0a0a0c] border-b border-white/10">
                <th className="p-4 font-mono text-[10px] tracking-widest text-muted-foreground w-32">VENDOR</th>
                {COLS.map((c) => <th key={c} className="p-4 font-mono text-[10px] tracking-widest text-muted-foreground">{c.toUpperCase()}</th>)}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.vendor} className={`border-b border-white/10 last:border-0 ${r.highlight ? "bg-primary/[0.06]" : "hover:bg-white/[0.02]"} transition-colors`}>
                  <td className="p-4 align-top">
                    <span className={`font-heading font-bold tracking-tight ${r.highlight ? "text-primary" : ""}`}>{r.vendor}</span>
                  </td>
                  {r.cells.map((cell, i) => <td key={i} className="p-4 align-top"><Cell value={cell} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-mono text-[11px] text-muted-foreground">Sources: vendor documentation, 2026. Comparison reflects typical/default configurations; specific editions vary.</p>
      </section>

      {/* Honest positioning */}
      <section className="max-w-5xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-6">
        <div className="card-elevated rounded-sm p-8">
          <div className="h-11 w-11 rounded-sm bg-[hsl(var(--chart-2))]/10 border border-[hsl(var(--chart-2))]/20 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--chart-2))]" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading text-xl font-bold mt-6 tracking-tight">Where Vaultvec wins</h2>
          <ul className="mt-5 space-y-3">
            {[
              "Purpose-built for pharma — 21 CFR Part 11, GAMP 5, GxP are the product, not a DIY project",
              "Tamper-evident, hash-chained audit trail with one-click integrity verification",
              "Client-side zero-knowledge encryption — even we can't read your data",
              "AI-generated, inspection-ready compliance reports",
              "Roles for IT, Compliance and Auditor with true segregation of duties",
              "Live in days, on your own Azure or object storage",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-[hsl(var(--chart-2))] shrink-0 mt-0.5" strokeWidth={2.5} /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="card-elevated rounded-sm p-8">
          <div className="h-11 w-11 rounded-sm bg-white/5 border border-border flex items-center justify-center">
            <Lock className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading text-xl font-bold mt-6 tracking-tight">Where the incumbents lead</h2>
          <ul className="mt-5 space-y-3">
            {[
              "Decades of backup-engine maturity: bare-metal, image-level, dedup at massive scale",
              "Broadest OS, hypervisor and SaaS coverage",
              "Established certifications (SOC 2, ISO 27001) and global 24/7 support",
              "Rubrik & Cohesity offer integrated immutable-by-architecture appliances",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-muted-foreground"><Minus className="h-4 w-4 text-[hsl(var(--chart-4))] shrink-0 mt-0.5" strokeWidth={2} /> {t}</li>
            ))}
          </ul>
          <p className="mt-5 text-xs text-muted-foreground leading-relaxed">We're upfront about this. If you need to protect thousands of mixed workloads at petabyte scale today, a mature platform may fit better. If your priority is <span className="text-foreground">provable pharma compliance</span>, that's exactly what Vaultvec is built for.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-t border-white/10 overflow-hidden">
        <div className="ambient-spot top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] opacity-20" />
        <div className="relative max-w-4xl mx-auto px-6 py-20 flex flex-col items-start gap-6">
          <h2 className="font-heading font-bold tracking-tighter text-3xl sm:text-4xl">Protecting GxP data? Let's talk.</h2>
          <p className="text-muted-foreground leading-relaxed max-w-xl">See how Vaultvec's compliance vault and zero-knowledge backups fit alongside — or replace — your current tools.</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/#how"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 text-white h-12 px-8 glow-primary">See how it works <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></Button></Link>
            <a href={MAILTO}><Button size="lg" variant="outline" className="rounded-sm border-white/15 h-12 px-8 hover:bg-white/5"><Mail className="mr-2 h-4 w-4" strokeWidth={1.5} /> Contact us</Button></a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
